import { useLayoutEffect, useRef } from 'react';

/*
 * Лёгкий слой мерцающих звёзд (DESIGN.md, «Движение»): 12 звёзд, у каждой
 * свой цикл 3,6–5,2 с и своя задержка, только opacity и transform. Мерцание
 * включается скриптом (класс sky-on) при наличии IntersectionObserver и без
 * prefers-reduced-motion, а вне экрана ставится на паузу (sky-live снимается).
 * Без этого звёзды просто стоят на месте. Слой не ловит клики и стоит под
 * текстом; звёзды лежат в полосе над заголовком и правее строки-подписи.
 * Рисунки — public/stars-a2.svg и stars-b2.svg; при смене рисунка файл
 * получает новое имя (stars-a3.svg), см. index.css.
 */
type Kind = 'big' | 'sm' | 'dot';

interface Star {
  left: string;
  top: string;
  size: number;
  kind: Kind;
  cycleS: number;
  delayS: number;
}

const STARS: readonly Star[] = [
  { left: '58%', top: '-34px', size: 22, kind: 'big', cycleS: 3.6, delayS: -0.4 },
  { left: '82%', top: '-30px', size: 18, kind: 'big', cycleS: 4.4, delayS: -2.1 },
  { left: '93%', top: '-8px', size: 20, kind: 'big', cycleS: 5.2, delayS: -3.3 },
  { left: '10%', top: '-28px', size: 12, kind: 'sm', cycleS: 3.8, delayS: -1.0 },
  { left: '33%', top: '-34px', size: 10, kind: 'sm', cycleS: 4.6, delayS: -2.5 },
  { left: '70%', top: '-12px', size: 12, kind: 'sm', cycleS: 4.0, delayS: -0.2 },
  { left: '47%', top: '-14px', size: 10, kind: 'sm', cycleS: 5.0, delayS: -4.1 },
  { left: '20%', top: '-14px', size: 5, kind: 'dot', cycleS: 3.9, delayS: -2.2 },
  { left: '40%', top: '-26px', size: 4, kind: 'dot', cycleS: 4.7, delayS: -3.4 },
  { left: '64%', top: '-24px', size: 5, kind: 'dot', cycleS: 3.7, delayS: -0.6 },
  { left: '88%', top: '-20px', size: 4, kind: 'dot', cycleS: 5.1, delayS: -4.4 },
  { left: '76%', top: '4px', size: 4, kind: 'dot', cycleS: 4.3, delayS: -1.5 },
];

export function StarSky() {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const layer = ref.current;
    if (!layer || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let observer: IntersectionObserver | undefined;
    try {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => layer.classList.toggle('sky-live', entry.isIntersecting));
        },
        { rootMargin: '80px 0px' },
      );
      observer.observe(layer);
      layer.classList.add('sky-on');
    } catch {
      // Мерцание — украшение: при сбое звёзды остаются неподвижными.
    }
    return () => {
      observer?.disconnect();
      layer.classList.remove('sky-on', 'sky-live');
    };
  }, []);

  return (
    <div ref={ref} className="sky" aria-hidden="true">
      {STARS.map((star) => (
        <span
          key={`${star.left}-${star.top}`}
          className={`sky-star sky-${star.kind}`}
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            ['--tw' as string]: `${star.cycleS}s`,
            ['--tw-delay' as string]: `${star.delayS}s`,
          }}
        />
      ))}
    </div>
  );
}
